/* @flow */

import type { ReadOnlyKeyValuePair } from '../jsHelpers/types';

import { AvatarSize } from '../../components/profiles/types';
import { TileConfigTileType } from '../../libs/netgemLibrary/v8/types/WidgetConfig';
import { getAppCss } from '../applicationCustomization/ui';

// Used to retrieve CSS variables defined at :root level
const styles = window.getComputedStyle(document.documentElement);

const getCssNumber = (name: string): number => parseInt(styles.getPropertyValue(name), 10);
const getCssString = (name: string): string => styles.getPropertyValue(name);

// Default max bitrate for green streaming when no value is given in app conf (in Mbps)
export const DEFAULT_MAX_BITRATE = 3.5;

// Possible values for skip duration (all levels, backward and forward versions)
export type SkipDuration = -60 | -30 | -10 | 10 | 30 | 60;

// Amount of time to skip backward or forward (in seconds)
export const SKIP_DURATION: ReadOnlyKeyValuePair<SkipDuration> = Object.freeze({
  Level0: 10,
  Level1: 30,
  Level2: 60,
});

// Constant widths and heights for different contexts
export const IMAGE_WIDTH: ReadOnlyKeyValuePair<number> = Object.freeze({
  Avenue: getCssNumber('--avenue-image-width-u'),
  AvenueHeader: 1920,
  CardBackground: 1920,
  CarouselBackground: 1920,
  Channel: getCssNumber('--channel-image-width-u'),
  ChannelConflict: getCssNumber('--conflict-channel-image-width-u'),
  Hub: 400,
  TileChannelLogo: getCssNumber('--tile-channel-logo-width-u'),
  TileNpvr: getCssNumber('--npvr-tv-image-width-u'),
  TileVod: getCssNumber('--tile-portrait-image-width-u'),
});

export const IMAGE_HEIGHT: ReadOnlyKeyValuePair<number> = Object.freeze({
  Avenue: getCssNumber('--avenue-image-height-u'),
  AvenueHeader: 1080,
  CardBackground: 1080,
  CarouselBackground: 1080,
  Channel: getCssNumber('--channel-image-height-u'),
  ChannelConflict: getCssNumber('--conflict-channel-image-height-u'),
  Hub: 64,
  TileChannelLogo: getCssNumber('--tile-channel-logo-height-u'),
  TileNpvr: getCssNumber('--npvr-tv-image-height-u'),
  TileVod: getCssNumber('--tile-portrait-image-height-u'),
});

export const TILE_SYNOPSIS_LINE_HEIGHT: number = getCssNumber('--tile-synopsis-line-height-u');
export const TILE_SYNOPSIS_TAGLINE_LINE_HEIGHT: number = getCssNumber('--tile-synopsis-tagline-line-height-u');

export const PROFILE_AVATAR_SIZE: ReadOnlyKeyValuePair<number> = Object.freeze({
  [AvatarSize.Big as string]: 135,
  [AvatarSize.Medium as string]: 100,
  [AvatarSize.Small as string]: 35,
});

export const BURGER_WIDTH: number = getCssNumber('--burger-width-u');

export const BUTTON_HOVER_COLORS: { Cursor: string, Main: string } = {
  Cursor: '#CE1866',
  Main: '#FE4E1E',
};

// All constants below are retrieved from CSS, through custom variables

// Imports app-specific CSS
getAppCss().then(() => {
  // Hover button colors
  BUTTON_HOVER_COLORS.Cursor = getCssString('--button-hover-cursor');
  BUTTON_HOVER_COLORS.Main = getCssString('--button-hover-main');
});

// Tile type: gemtv
const TV_TILE_PLUS_MARGIN: number = getCssNumber('--tile-gemtv-image-width-u') + getCssNumber('--tile-gemtv-horizontal-space-u');

// Tile type: portrait
const VOD_TILE_WIDTH_PLUS_MARGIN: number = IMAGE_WIDTH.TileVod + getCssNumber('--tile-portrait-horizontal-space-u');

// Tile type: channel
const CHANNEL_TILE_WIDTH_PLUS_MARGIN = IMAGE_WIDTH.Channel + getCssNumber('--tile-channel-horizontal-space-u');

// Tile type: channelgroup
const CHANNEL_GROUP_TILE_WIDTH_PLUS_MARGIN = getCssNumber('--tile-channel-group-image-width-u') + getCssNumber('--tile-channel-group-horizontal-space-u');

// Tile type: channelgroup.big
const CHANNEL_GROUP_BIG_TILE_WIDTH_PLUS_MARGIN = getCssNumber('--tile-channel-group-big-image-width-u') + getCssNumber('--tile-channel-group-big-horizontal-space-u');

// Tile type: deeplink
const DEEPLINK_TILE_WIDTH_PLUS_MARGIN: number = getCssNumber('--tile-deeplink-image-width-u') + getCssNumber('--tile-deeplink-horizontal-space-u');

// Tile type: deeplink.3x1
const DEEPLINK_3X1_TILE_WIDTH_PLUS_MARGIN: number = getCssNumber('--tile-deeplink-3x1-image-width-u') + getCssNumber('--tile-deeplink-3x1-horizontal-space-u');

// Tile type: deeplink.5x1
const DEEPLINK_5X1_TILE_WIDTH_PLUS_MARGIN: number = getCssNumber('--tile-deeplink-5x1-image-width-u') + getCssNumber('--tile-deeplink-5x1-horizontal-space-u');

// Tile type: landscape.big
const LANDSCAPE_BIG_TILE_WIDTH_PLUS_MARGIN: number = getCssNumber('--tile-landscape-big-image-width-u') + getCssNumber('--tile-landscape-big-horizontal-space-u');

// Tile type: landscape.vod
const LANDSCAPE_VOD_TILE_WIDTH_PLUS_MARGIN: number = getCssNumber('--tile-landscape-vod-image-width-u') + getCssNumber('--tile-landscape-vod-horizontal-space-u');

const getTileWidthPlusMargin = (tileType?: TileConfigTileType): number => {
  if (tileType === undefined) {
    return 0;
  }

  switch (tileType) {
    case TileConfigTileType.Channel:
      return CHANNEL_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.ChannelGroup:
      return CHANNEL_GROUP_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.ChannelGroupBig:
      return CHANNEL_GROUP_BIG_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.ChannelGroupBigFill:
      return CHANNEL_GROUP_BIG_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.ChannelGroupFill:
      return CHANNEL_GROUP_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.Deeplink:
      return DEEPLINK_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.Deeplink3x1:
      return DEEPLINK_3X1_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.Deeplink5x1:
      return DEEPLINK_5X1_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.Gemtv:
      return TV_TILE_PLUS_MARGIN;
    case TileConfigTileType.GemtvBig:
      return LANDSCAPE_BIG_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.GemtvMedium:
      return TV_TILE_PLUS_MARGIN;
    case TileConfigTileType.Landscape:
      return TV_TILE_PLUS_MARGIN;
    case TileConfigTileType.LandscapeBig:
      return LANDSCAPE_BIG_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.LandscapeVod:
      return LANDSCAPE_VOD_TILE_WIDTH_PLUS_MARGIN;
    case TileConfigTileType.Portrait:
      return VOD_TILE_WIDTH_PLUS_MARGIN;

    // No default
  }
};

export const EPG = Object.freeze({
  // EPG for all channels is retrieved by chunks to avoid missing programs
  ChannelChunkSize: 20,
  // Range of loaded live feed EPG (3 hours in milliseconds)
  FeedRange: 10_800_000,
  // Cached live feed is updated every 10 minutes (in milliseconds)
  IntervalCachedLiveFeed: 600_000,
  // Live feed is updated every 10 seconds (in milliseconds)
  IntervalLiveFeedEpgReady: 10_000,
  // Live feed is updated every 100 milliseconds when EPG manager is refreshing (in milliseconds)
  IntervalLiveFeedEpgRefreshing: 100,
  // How long the live feed EPG go back in time (2 hours in milliseconds)
  RewindPeriod: 7_200_000,
});

export const TOOLTIP = Object.freeze({
  DefaultCloseDelay: 150,
  DefaultMaxWidth: 600,
  DefaultOffsetValue: 8,
  DefaultOpenDelay: 150,
  DefaultZIndex: 100_000,
  GroupTimeout: 500,
});

export { getTileWidthPlusMargin };
